import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { fetchSalesProgress } from '../../../services/salesInvoiceService';
import { cardStyle } from '../../../helpers/Executive-Dashboard/styleFormat';
import { fmtNum } from '../../../helpers/Executive-Dashboard/formatters';
import { integration } from '../../../helpers/Executive-Dashboard/integration';

export const SalesProgressCard = React.memo(({ sourceLabel, branch }) => {
    
    const [viewMode, setViewMode] = useState('permonth');
    const now = new Date();
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(now.getFullYear());
    const [progressData, setProgressData] = useState(null);
    const [progressLoading, setProgressLoading] = useState(true);
    const [progressError, setProgressError] = useState(null);
    const [slideDirection, setSlideDirection] = useState('none'); // 'left' | 'right' | 'none'
    const [slideKey, setSlideKey] = useState(0);

    const loadSalesProgress = async (m, y) => {
        setProgressLoading(true);
        setProgressError(null);
        try {
            const config = sourceLabel === 'Gelora Printing' ? integration('GP') : integration('SUMA');
            const result = await fetchSalesProgress(m, y, viewMode, config, branch);
            setProgressData(result);
        } catch (err) {
            setProgressError(err.message);
        } finally {
            setProgressLoading(false);
        }
    };

    useEffect(() => {
        loadSalesProgress(month, year);
    }, [month, year, sourceLabel, viewMode, branch]);

    const handlePrev = () => {
        setSlideDirection('right');
        setSlideKey(k => k + 1);
        if (month === 1) { setMonth(12); setYear(y => y - 1); }
        else setMonth(m => m - 1);
    };

    const handleNext = () => {
        setSlideDirection('left');
        setSlideKey(k => k + 1);
        if (month === 12) { setMonth(1); setYear(y => y + 1); }
        else setMonth(m => m + 1);
    };

    // const currentData = viewMode === 'all' ? aggregatedData : dummySalesDataArray[currentIndex];
    const currentData = progressData || { revenue: 0, paid: 0, unpaidInvoices: 0, unpaid: 0, notDue: 0, overdue: 0, dateRange: '' };

    const leftTotal = currentData.revenue || 1;
    const paidPct = (currentData.paid / leftTotal) * 100;
    const unpaidInvPct = (currentData.unpaidInvoices / leftTotal) * 100;

    const rightTotal = currentData.unpaid || 1;
    const notDuePct = (currentData.notDue / rightTotal) * 100;
    const overduePct = (currentData.overdue / rightTotal) * 100;

    const animName = slideDirection === 'left' ? 'sp-slide-left' : slideDirection === 'right' ? 'sp-slide-right' : 'sp-fade-in';

    return (
        <div style={{ ...cardStyle, marginBottom: '16px', padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>Sales Progress</h3>
                
                <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '4px' }}>
                    <button 
                        onClick={() => setViewMode('all')}
                        style={{ 
                            padding: '6px 14px', borderRadius: '6px', border: 'none', 
                            fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                            background: viewMode === 'all' ? '#fff' : 'transparent',
                            color: viewMode === 'all' ? '#0ea5e9' : '#64748b',
                            boxShadow: viewMode === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s'
                        }}>
                        All
                    </button>
                    <button 
                        onClick={() => setViewMode('permonth')}
                        style={{ 
                            padding: '6px 14px', borderRadius: '6px', border: 'none', 
                            fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                            background: viewMode === 'permonth' ? '#fff' : 'transparent',
                            color: viewMode === 'permonth' ? '#0ea5e9' : '#64748b',
                            boxShadow: viewMode === 'permonth' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s'
                        }}>
                        Per Month
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes sp-slide-left {
                    0%   { opacity: 0; transform: translateX(40px); }
                    100% { opacity: 1; transform: translateX(0); }
                }
                @keyframes sp-slide-right {
                    0%   { opacity: 0; transform: translateX(-40px); }
                    100% { opacity: 1; transform: translateX(0); }
                }
                @keyframes sp-fade-in {
                    0%   { opacity: 0; }
                    100% { opacity: 1; }
                }
            `}</style>
            
            <div style={{ padding: '20px' }}>
                

                <div key={`content-${slideKey}`} style={{ animation: `${animName} 0.4s ease-out` }}>
                {progressLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '13px' }}>Loading...</div>
                ) : progressError ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '8px' }}>{progressError}</div>
                        <button onClick={() => loadSalesProgress(month, year)} style={{ padding: '6px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Retry</button>
                    </div>
                ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
                    {/* Left Side */}
                    <div>
                        {viewMode === 'permonth' && (
                            <div className="flex justify-end relative mb-[30px]">
                                <div className="flex items-center gap-[10px] text-sky-500 font-semibold text-[13px]">
                                    <div
                                        onClick={handlePrev}
                                        className="flex items-center justify-center w-[26px] h-[26px] rounded-[6px] bg-white cursor-pointer text-[14px] font-bold text-slate-500 transition-all border border-slate-200 hover:bg-slate-100 hover:text-slate-700"
                                    >
                                        <ArrowLeft size={20} />
                                    </div>

                                    <span key={`label-${slideKey}`} className="text-[14px] text-sky-500 min-w-[180px] text-center" style={{ animation: `${animName} 0.35s ease-out` }}>
                                        {progressLoading ? '...' : (currentData.dateRange || `${month}/${year}`)}
                                    </span>

                                    <div
                                        onClick={handleNext}
                                        className="flex items-center justify-center w-[26px] h-[26px] rounded-[6px] bg-white cursor-pointer text-[14px] font-bold text-slate-500 transition-all border border-slate-200 hover:bg-slate-100 hover:text-slate-700"
                                    >
                                        <ArrowRight size={20} />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '24px', flexWrap: 'wrap', gap: '8px' }}>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>Pendapatan</div>
                            <div style={{ fontSize: '24px', fontWeight: '600', color: '#0f172a' }}>
                                <span style={{fontSize: '18px', fontWeight: '500', marginRight: '4px'}}>Rp</span>
                                {fmtNum(currentData.revenue)}
                            </div>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '15px', color: '#94a3b8', marginBottom: '10px' }}>Faktur Lunas</div>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: '#4ade80' }}>
                                    <span style={{fontSize: '14px', fontWeight: '500', marginRight: '2px'}}>Rp</span>{fmtNum(currentData.paid)}
                                </div>
                            </div>
                            <div style={{ flex: 1, textAlign: 'right' }}>
                                <div style={{ fontSize: '15px', color: '#94a3b8', marginBottom: '10px' }}>Faktur Belum Lunas</div>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: '#fbbf24' }}>
                                    <span style={{fontSize: '14px', fontWeight: '500', marginRight: '2px'}}>Rp</span>{fmtNum(currentData.unpaidInvoices)}
                                </div>
                            </div>
                        </div>
                        
                        {/* Progress Bar Left */}
                        <div style={{ height: '40px', display: 'flex', width: '100%', overflow: 'hidden' }}>
                            <div style={{ width: `${paidPct}%`, backgroundColor: '#4ade80', transition: 'width 0.5s ease-in-out' }}></div>
                            <div style={{ width: '4px', backgroundColor: '#fff' }}></div>
                            <div style={{ width: `${unpaidInvPct}%`, backgroundColor: '#fcd34d', transition: 'width 0.5s ease-in-out' }}></div>
                        </div>
                    </div>

                    {/* Right Side */}
                    <div>
                        {viewMode === 'permonth' && (
                            <div className="flex justify-end relative mb-[35px]">
                                <span className={`text-[14px] text-sky-500 font-semibold`}>Hari Ini</span>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '24px', flexWrap: 'wrap', gap: '8px' }}>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>Belum Lunas</div>
                            <div style={{ fontSize: '24px', fontWeight: '600', color: '#0f172a' }}>
                                <span style={{fontSize: '18px', fontWeight: '500', marginRight: '4px'}}>Rp</span>
                                {fmtNum(currentData.unpaid)}
                            </div>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '15px', color: '#94a3b8', marginBottom: '10px' }}>Belum Jatuh Tempo</div>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: '#fbbf24' }}>
                                    <span style={{fontSize: '14px', fontWeight: '500', marginRight: '2px'}}>Rp</span>{fmtNum(currentData.notDue)}
                                </div>
                            </div>
                            <div style={{ flex: 1, textAlign: 'right' }}>
                                <div style={{ fontSize: '15px', color: '#94a3b8', marginBottom: '10px' }}>Lewat Jatuh Tempo</div>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: '#ef4444' }}>
                                    <span style={{fontSize: '14px', fontWeight: '500', marginRight: '2px'}}>Rp</span>{fmtNum(currentData.overdue)}
                                </div>
                            </div>
                        </div>
                        
                        {/* Progress Bar Right */}
                        <div style={{ height: '40px', display: 'flex', width: '100%', overflow: 'hidden' }}>
                            <div style={{ width: `${notDuePct}%`, backgroundColor: '#fcd34d', transition: 'width 0.5s ease-in-out' }}></div>
                            <div style={{ width: '4px', backgroundColor: '#fff' }}></div>
                            <div style={{ width: `${overduePct}%`, backgroundColor: '#ef4444', transition: 'width 0.5s ease-in-out' }}></div>
                        </div>
                    </div>
                </div>
                )}
                </div>
            </div>
        </div>
    );
});